import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import styled from "@emotion/styled";
import { Button, Form, Input, InputNumber, message, Modal, Popover, Space, Spin, Table, Tag } from "antd";
import { AxiosResponse } from "axios";
import { FC, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ResponseEntity } from "../../../common/entity/response.entity";
import { checkPermission } from "../../../module/admin/service/permission.service";
import { CouponEntity } from "../../../module/coupon/entity/coupon.entity";
import { CouponParam } from "../../../module/coupon/param/coupon.param";
import subscriptionCouponService from "../../../module/subscriptionCoupon/service";

const { confirm } = Modal;
export const SubscriptionCouponList: FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const [couponTitle, setCouponTitle] = useState<string>("");
	const [searchObj, setSearchObj] = useState<CouponParam>({ title: "", customPageSize: 15, customPageNum: 1 });
	const [couponList, setCouponList] = useState<CouponEntity[]>([]);
	const [total, setTotal] = useState<number>(0);

	const init = useCallback(async () => {
		const { data } = await subscriptionCouponService.list({ ...searchObj });
		if (data.code === 200) {
			setCouponList(data.data.list);
			setTotal(data.data.total);
		}
	}, [searchObj]);

	useEffect(() => {
		init();
	}, [init]);

	const deleteCoupon = (id: number) => {
		confirm({
			title: t("confirmOperation"),
			okText: "Yes",
			okType: "danger",
			cancelText: "No",
			onOk: async () => {
				const { data }: AxiosResponse<ResponseEntity> = await subscriptionCouponService.delete(id);
				if (data.code === 200) {
					message.success(data.msg);
					init();
				}
			},
			onCancel() {
				console.log("Cancel");
			},
		});
	};

	const changeStatus = async (id: number, record: any) => {
		const { data } = await subscriptionCouponService.update(id, { ...record, status: record.status == 1 ? 2 : 1 });
		if (data.code == 200) {
			message.success(data.msg);
			init();
		}
	};

	const columns = [
		{
			title: "id",
			dataIndex: "id",
		},
		{
			title: t("title"),
			dataIndex: "title",
		},
		{
			title: t("couponCode"),
			dataIndex: "code",
		},
		{
			width: 130,
			title: t("expirationTime"),
			render: (record: CouponEntity) => {
				return record.effectiveWay == 1 ? record.expirationTime : record.effectiveDay + "天";
			},
		},
		{
			title: t("limitNum"),
			dataIndex: "limitNum",
		},
		{
			title: t("remainingNum"),
			dataIndex: "remainingNum",
		},
		{
			title: t("grantNum"),
			dataIndex: "grantNum",
		},
		{
			title: t("worth"),
			dataIndex: "worth",
			render: (text: number) => {
				return text! && text + "元";
			},
		},
		{
			title: t("status"),
			dataIndex: "status",
			render: (text: number) => {
				return text === 1 ? <Tag color={"green"}>{t("onSale")}</Tag> : <Tag color={"red"}>{t("off")}</Tag>;
			},
		},
		{
			title: t("action"),
			render: (_text: string, record: CouponEntity) => {
				return (
					<div>
						<div className='Line'>
							<Space>
								{checkPermission(73) && (
									<Button
										type='default'
										onClick={() => {
											navigate(`/subscription-coupon/modify/${record?.id}`);
										}}
									>
										{t("edit")}
									</Button>
								)}

								{record.status === 1 && checkPermission(75) && (
									<Button
										type='primary'
										onClick={() => {
											changeStatus(Number(record.id), record);
										}}
									>
										{t("ban")}
									</Button>
								)}
								{record.status === 2 && checkPermission(75) && (
									<Button
										type='primary'
										onClick={() => {
											changeStatus(Number(record.id), record);
										}}
									>
										{t("enable")}
									</Button>
								)}
								{checkPermission(115) && (
									<Button
										type='primary'
										danger
										onClick={() => {
											record.id && deleteCoupon(record.id);
										}}
									>
										{t("delete")}
									</Button>
								)}
							</Space>
						</div>
					</div>
				);
			},
		},
	];
	return (
		<Container>
			<div>
				<Space>
					<Space>
						{t("couponName")}
						<Input placeholder={t("enterCouponName")} allowClear onChange={(e) => setCouponTitle(e.target.value)} />
					</Space>
				</Space>
				<div style={{ marginTop: "20px" }}>
					<Space>
						<Button type={"primary"} icon={<SearchOutlined />} onClick={() => setSearchObj({ ...searchObj, title: couponTitle })}>
							{t("search")}
						</Button>
						{checkPermission(73) && (
							<Button type={"primary"} icon={<PlusOutlined />} onClick={() => navigate("/subscription-coupon/modify")}>
								{t("add")}
							</Button>
						)}
					</Space>
				</div>
			</div>

			<Table
				columns={columns}
				dataSource={couponList}
				pagination={{
					pageSize: searchObj.customPageSize,
					current: searchObj.customPageNum,
					total: total,
					showTotal: () => {
						return (
							<div>
								{t("total")} {total}
							</div>
						);
					},
				}}
				style={{ width: "100%" }}
				rowKey={(record) => record.id + ""}
				onChange={(pagination) => {
					setSearchObj((previousData) => {
						return {
							...previousData,
							customPageNum: pagination.current,
							customPageSize: pagination.pageSize,
						};
					});
				}}
			/>
		</Container>
	);
};
const Container = styled.div`
	.el-radio__original {
		display: none !important; /* 隐藏原生 radio 输入，但仍然允许交互 */
	}
	width: 100%;
	padding: 10px;

	.headers {
		width: 100%;

		.searchList {
			width: 100%;
			display: flex;
			align-items: center;
			margin-bottom: 15px;

			.searchItem {
				min-width: 150px;
				text-align: right;
			}
		}
	}

	.Line {
		margin-bottom: 10px;
	}
`;
